import { Alert, cn, Skeleton } from '@hvac/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { equipmentDetailQuery } from '../../shared/api/parties-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';
import { ObjectTimeline, RelationshipsPanel } from '../objects/object-panels.js';
import { warrantyTone } from './equipment-page.js';

/**
 * One unit. What it is, where it lives, when its two warranties run out,
 * then the same panels every object gets.
 */
export function EquipmentDetailPage() {
  const { equipmentId } = useParams({ from: '/protected/equipment/$equipmentId' });
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const unit = useQuery(equipmentDetailQuery(organizationId, equipmentId));
  const today = new Date();

  if (unit.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (unit.isError) {
    return (
      <Alert tone="danger" title="That equipment could not be loaded">
        {unit.error.message}
      </Alert>
    );
  }

  const data = unit.data;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/equipment" className="text-sm text-content-muted hover:text-content">
          ← Equipment
        </Link>
        <h1 className="mt-1 text-xl font-semibold text-content">{data.label}</h1>
        <p className="text-sm text-content-muted">
          {[data.serialNumber, data.customerName, data.propertyAddress, data.locationNote]
            .filter((part) => part !== null)
            .join(' · ') || 'No placement details on file.'}
          {data.status === 'active' ? '' : ` · ${data.status}`}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Installed" value={data.installedOn ?? '-'} />
        <Stat
          label="Parts warranty until"
          value={data.partsWarrantyExpiresOn ?? 'unknown'}
          tone={warrantyTone(data.partsWarrantyExpiresOn, today)}
        />
        <Stat
          label="Labour warranty until"
          value={data.labourWarrantyExpiresOn ?? 'unknown'}
          tone={warrantyTone(data.labourWarrantyExpiresOn, today)}
        />
      </div>

      <RelationshipsPanel
        organizationId={organizationId}
        entityType="equipment"
        entityId={data.id}
      />

      <ObjectTimeline
        organizationId={organizationId}
        resourceType="equipment"
        resourceId={data.id}
      />
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-[var(--radius-surface)] border border-border bg-surface p-3">
      <p className="text-xs text-content-muted">{label}</p>
      <p className={cn('mt-0.5 text-base font-medium', tone ?? 'text-content')}>{value}</p>
    </div>
  );
}
